import { IWidgetDefinition, IWidget, IBaseSettings, IWidgetSettings } from '../../common/core';

var template = require("./template.html");
var styles = require("./styles.scss").toString();

/**
* Settings
*/
export interface IFieldDatePickerSettings extends IBaseSettings {

  /**
  * Title ("Date Picker" by default)
  */
  "label": string;

  /**
  * Field
  * `string | ((widget) => string)`
  */
  "field": string | ((widget) => string);

  /**
   * Subscribe to viz zoom
   * true - for all viz widgets
   * array - ids of viz widgets
   */
  "zoom"?: boolean | string[];

  /**
   * DatePicker options
   */
  "datePickerOptions"?: any;

  /**
   * From label
   */
  "fromLabel"?: string;

  /**
   * To label
   */
  "toLabel"?: string;

  /**
   * DatePicker labels
   */
  "datePickerLabels"?: any;
}

/**
* State
*/
export interface IFieldDatePickerState {
  /**
   * df
   */
  from?: string;

  /**
   * dt
   */
  to?: string;

  /**
   * Collapsed
   */
  collapsed?: boolean;

  /**
   * When zoom here is original df
   */
  zoomFrom?: string;

  /**
   * When zoom here is original dt
   */
  zoomTo?: string;
}

export interface IFieldDatePickerWidgetSettings extends IWidgetSettings<IFieldDatePickerSettings, IFieldDatePickerState> {
  type: "field-date-picker"
}

/**
 * Data
 */
export class FieldDatePickerData {

  chartMessageListener: any;
  theme?: string;

  constructor(public widget: IWidget<FieldDatePickerData, IFieldDatePickerSettings, IFieldDatePickerState>) {
    this.theme = this.widget.service.getGlobalOptions().theme;
  }

  _datePickerOptions: any = null;
  get datePickerOptions() {
    if (!this._datePickerOptions) {
      this._datePickerOptions = {
        inline: true,
        dateFormat: 'yyyy-mm-dd',
        indicateInvalidDate: false,
        openSelectorOnInputClick: true,
        showClearDateBtn: false,
        showSelectorArrow: false,
        selectorHeight: 'auto',
        selectorWidth: 'auto',
        sunHighlight: true,
        ...this.widget.settings.datePickerOptions
      };
    }
    return this._datePickerOptions;
  }

  collapse() {
    if (this.widget['grid']) {
      return;
    }
    this.widget.state.collapsed = !this.widget.state.collapsed;
    this.widget.service.setState(true);
  }

  applyNewDates(dfDt) {
    switch (dfDt) {
      case 'df':
        if (!this.widget.state.from) {
          this.widget.state.from = undefined;
        }
        break;
      case 'dt':
        if (!this.widget.state.to) {
          this.widget.state.to = undefined;
        }
        break;
    }
    this.clearZoom();
    this.widget.service.setState();
  }

  get field() {
    if (typeof this.widget.settings.field == 'function') {
      return this.widget.settings.field(this.widget);
    }
    return this.widget.settings.field;
  }

  prepareDate(date) {
    if (date && typeof date == 'string') {
      return date.split('.')[0];
    }
    return date;
  }

  calendarShown: {
    type: 'from' | 'to',
    top?: number,
    bottom?: number,
    left?: number,
    right?: number,
  } = null;
  toggleCalendar(type, inputElement) {
    if (this.calendarShown && this.calendarShown.type === type) {
      this.calendarShown = null;
    } else {
      var top = 30;
      var left = 0;

      var inputWrapperEl = inputElement.parentElement;
      while (inputWrapperEl && !inputWrapperEl.classList.contains('sj-dt-input-wrapper')) {
        inputWrapperEl = inputWrapperEl.parentElement;
      }

      // calculate position based on row element (instead of df/dt container).
      // this is for cases when df/dt inputs shown in one line
      /*
      if (inputWrapperEl.offsetWidth < 200) {
        while (inputWrapperEl && !inputWrapperEl.classList.contains('sj-fdp-container')) {
          inputWrapperEl = inputWrapperEl.parentElement;
        }
      }
        */

      var el = inputWrapperEl;
      while (el && !el.classList.contains('sj-widget-content-container')) {
        top += el.offsetTop;
        left += el.offsetLeft;
        el = el.offsetParent;
      }
      top -= el.scrollTop;

      this.calendarShown = {
        type: type,
        top: top,
        right: el.offsetWidth - (left + inputWrapperEl.offsetWidth)
      };

      // change alignment from right to left if there is no space at the left side
      if (inputWrapperEl.getBoundingClientRect().x + left + inputWrapperEl.offsetWidth < (this.theme ? 350 : 300)) {
        this.calendarShown.left = left;
        this.calendarShown.right = null;
      }

      // change alignment from bottom to top if there is not enough space at the bottom
      var box = inputWrapperEl.getBoundingClientRect();
      var bodyHeight = document.body.offsetHeight;
      if (bodyHeight - box.y - 30 < 380) {
        this.calendarShown.bottom = el.offsetHeight - top + 40;
        this.calendarShown.top = null;
      }
    }
  }

  toggleMenu(e, id) {

    if (!e.shown && this.calendarShown && this.calendarShown.type !== id) {
      return;
    }

    if (e.shown) {
      this.toggleCalendar(id, e.input);
    } else {
      this.calendarShown = null;
    }
  }

  clearVizZoom() {
    this.widget.service.getAllWidgets().forEach(w => {
      if (w.type == 'viz' && this.widget.settings) {
        if (this.widget.settings.zoom === true || (Array.isArray(this.widget.settings.zoom) && this.widget.settings.zoom.indexOf(w.id) != -1)) {
          if (w.state.zoom) {
            delete w.state.zoom;
          }
        }
      }
    });
  }

  clearZoom(setState?: boolean) {
    delete this.widget.state.zoomFrom;
    delete this.widget.state.zoomTo;

    this.clearVizZoom();

    if (setState) {
      this.widget.service.setState();
    }
  }

  settingsChanged() {
    this._datePickerOptions = null;
    this.unsubscribe();
    this.subscribe();
  }

  subscribe() {
    if (this.widget.settings.zoom) {
      this.chartMessageListener = (e) => {
        if (e.data && typeof e.data == 'string') {
          var msg: any = {};
          try {
            msg = JSON.parse(e.data);
          } catch (e) { }
          if (msg.messageType == 'chartZoom') {
            if (this.widget.settings.zoom === true ||
              (Array.isArray(this.widget.settings.zoom) && this.widget.settings.zoom.indexOf(msg.chart) != -1)) {
              if (msg.zoom.x1 && msg.zoom.x2) {
                // set zoom

                // var zoomX1Date = window['moment'].utc(msg.zoom.x1).valueOf();
                var zoomX1Date = this.prepareDate(msg.zoom.x1);

                // var zoomX2Date = window['moment'].utc(msg.zoom.x2).valueOf();
                var zoomX2Date = this.prepareDate(msg.zoom.x2);

                this.widget.state.zoomFrom = zoomX1Date;
                this.widget.state.zoomTo = zoomX2Date;
              } else {
                this.clearZoom();
              }
              this.widget.service.setState(true);
            }
          }
        }
      }
    };
    window.addEventListener('message', this.chartMessageListener);
  }

  unsubscribe() {
    window.removeEventListener('message', this.chartMessageListener);
  }

  afterInit() {
    this.subscribe();
  }

  destroy() {
    this.unsubscribe();
  }

  getQueryPart(field: string, date: string, sign: '>' | '<', minMax: 'MIN' | 'MAX') {
    if (date && date != minMax) {
      if (date.endsWith('@excl')) {
        return this.widget.service.helpers.formatQueryPart(field, date.replace('@excl', ''), sign);
      } else {
        return this.widget.service.helpers.formatQueryPart(field, date, `${sign}=`);
      }
    }
  }

  getQuery() {
    var from = this.widget.state.zoomFrom || this.widget.state.from;
    var to = this.widget.state.zoomTo || this.widget.state.to;
    var query = [
      this.getQueryPart(this.field, from, '>', 'MIN'),
      this.getQueryPart(this.field, to, '<', 'MAX')
    ].filter(q => q).join(' ');
    return query;
  }
}

var widgetDefinition: IWidgetDefinition<FieldDatePickerData, IFieldDatePickerSettings, IFieldDatePickerState> = {
  init: function (widget) {
    widget.data = new FieldDatePickerData(widget);
  },
  afterInit: function (w) {
    w.data.afterInit();
  },
  getQuery: function (widget) {
    return widget.data.getQuery();
  },
  destroy: function (w) {
    w.data.destroy && w.data.destroy();
  },
  styles,
  template,
  classNames: ['sj-widget-rel']
};

export { widgetDefinition };
